import React, { useState } from 'react';
import * as Yup from 'yup';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { addMinutes, isAfter, isBefore } from 'date-fns';
import { Form, Formik } from 'formik';
import { useDebounceCallback } from 'usehooks-ts';
import { string } from 'yup';
import { ReoccurrenceEnum, Session, TypeA36Enum } from '@juno/client-api/model';
import { DrawerHeader, GenericFormikInput, RightExpandableDrawer } from '@juno/ui';
import {
  addDurationToDate,
  calculateAndSetEndDate,
  generateTimeSlots,
  handleStartDateChange,
  normalizeDateStartAndEnd,
  parseTimeStringToDate,
  slugify,
} from '@juno/utils';
import { DurationPicker } from './helpers/DurationPicker';
import { StartTimePicker } from './helpers/StartTimePicker';
import { StyledMenuItem } from './helpers/utils';

interface CreateNewSessionDrawerProps {
  open: boolean;
  onSave: (session: Session) => void;
  onClose: () => void;
  isSaving: boolean;
  allSessions: Session[];
  eventType: TypeA36Enum;
}

const CreateNewSessionDrawer: React.FC<CreateNewSessionDrawerProps> = ({
  open,
  onClose,
  onSave,
  isSaving,
  allSessions,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [duration, setDuration] = useState(30);
  const currentDate = new Date();
  currentDate.setSeconds(0);
  currentDate.setMilliseconds(0);

  const validationSchema = Yup.object().shape({
    title: string().max(90, 'Title character limit is 90').required('Title is required'),
    slug: string()
      .max(90, 'Slug character limit is 90')
      .required('Slug is required')
      .test(
        'slug_no_space_or_special',
        'Slug cannot have spaces or special characters',
        function test(value) {
          if (value && value.length > 0) {
            return !/[^a-zA-Z0-9-_]/.test(value);
          } else {
            return true;
          }
        },
      )
      .test('title-unique-validation', 'This field must be unique', function test(value) {
        if (value && value.length > 0) {
          const filteredSessions = allSessions?.filter((c) => c.slug === value) ?? [];
          return filteredSessions.length <= 0;
        } else {
          return true;
        }
      }),
    date_start: Yup.string()
      .required('Start date is required')
      .test('date_start', 'Start date must be before end date', function test(this, value) {
        if (!this?.parent?.date_end) return true;
        if (value) {
          return isBefore(new Date(value), new Date(this?.parent?.date_end));
        } else {
          return false;
        }
      }),
    date_end: Yup.string().test(
      'date_end',
      'End date must be after the start date',
      function test(this, value) {
        if (value) {
          return isAfter(new Date(value), new Date(this?.parent?.date_start));
        } else {
          return true;
        }
      },
    ),
  });

  const [initialFormState] = useState<Session>(() => {
    const currentDate = new Date();
    currentDate.setSeconds(0);
    currentDate.setMilliseconds(0);
    return {
      date_start: currentDate.toISOString(),
      date_end: addMinutes(currentDate, 30).toISOString(),
      reoccurrence: ReoccurrenceEnum.once,
      time_end: addMinutes(currentDate, 30).toISOString(),
    } as Session;
  });

  const debouncedSetSlug = useDebounceCallback((value: string, setFieldValue: any) => {
    setFieldValue('slug', slugify(value));
  }, 100);

  return (
    <Formik
      initialValues={initialFormState}
      validationSchema={validationSchema}
      onSubmit={(values) => onSave(values)}
      enableReinitialize={false}
    >
      {(props) => {
        const {
          values,
          errors,
          handleChange,
          setFieldValue,
          dirty,
          isValid,
          resetForm,
          setFieldTouched,
          handleSubmit,
          setValues,
        } = props;
        const handleOnClose = () => {
          setActiveStep(0);
          resetForm();
          onClose();
        };
        const isRecurring = values.reoccurrence !== ReoccurrenceEnum.once;

        const steps = isRecurring
          ? [
              'What’s the name of your Session?',
              'Does the Session repeat?',
              'What day does the Series start?',
              'What time does the Session start?',
              'When does the Series end?',
            ]
          : [
              'What’s the name of your Session?',
              'Does the Session repeat?',
              'What day does the Session start?',
              'What time does the Session start?',
            ];

        const nextDisabled = () => {
          if (activeStep === 0) {
            return !values.title || !values.slug;
          } else if (activeStep === 1) {
            return !values.reoccurrence;
          } else if (activeStep === 2) {
            return !values.date_start;
          } else if (activeStep === 3) {
            return !values.date_start;
          }
          return false;
        };

        const isLastStep = activeStep === steps.length - 1;

        const recurrenceStartSubtext = isRecurring
          ? `You are scheduling an event that recurs ${values.reoccurrence}. The date selected here indicates when the first event in the series begins.`
          : 'You are scheduling a one-time event. The date and time selected here represent when this event starts.';

        return (
          <Form>
            <RightExpandableDrawer open={open} onClose={handleOnClose}>
              <DrawerHeader title={'Create New Session'} />
              <Stepper orientation='vertical' activeStep={Math.min(activeStep, steps.length - 1)}>
                {steps.map((label, index) => (
                  <Step key={label} completed={activeStep > index}>
                    <StepLabel>
                      <Typography fontWeight={index === activeStep ? 'bold' : 'normal'}>
                        {label}
                        {index === 0 && activeStep > 0 && values.title && (
                          <Typography variant='caption' sx={{ ml: 1, color: 'text.secondary' }}>
                            {values.title}
                          </Typography>
                        )}

                        {index === 1 && activeStep > 1 && values.reoccurrence && (
                          <Typography variant='caption' sx={{ ml: 1, color: 'text.secondary' }}>
                            {values.reoccurrence === ReoccurrenceEnum.once
                              ? 'Does not repeat'
                              : values.reoccurrence === ReoccurrenceEnum.daily
                              ? 'Daily'
                              : values.reoccurrence === ReoccurrenceEnum.weekly
                              ? 'Weekly'
                              : values.reoccurrence === ReoccurrenceEnum.monthly
                              ? 'Monthly'
                              : values.reoccurrence === ReoccurrenceEnum.yearly
                              ? 'Yearly'
                              : ''}
                          </Typography>
                        )}

                        {index === 2 && activeStep > 2 && values.date_start && (
                          <Typography variant='caption' sx={{ ml: 1, color: 'text.secondary' }}>
                            {new Date(values.date_start).toLocaleString([], {
                              dateStyle: 'long',
                            })}
                          </Typography>
                        )}

                        {index === 3 && activeStep > 3 && values.date_start && (
                          <Typography variant='caption' sx={{ ml: 1, color: 'text.secondary' }}>
                            {new Date(values.date_start).toLocaleTimeString('en-US', {
                              hour: 'numeric',
                              minute: '2-digit',
                            })}
                            {' for '}
                            {(() => {
                              const mins = duration;
                              const hours = Math.floor(mins / 60);
                              const minutes = mins % 60;

                              const hourStr =
                                hours > 0 ? `${hours} Hour${hours > 1 ? 's' : ''}` : '';
                              const minStr =
                                minutes > 0 ? `${minutes} Min${minutes > 1 ? 's' : ''}` : '';

                              return [hourStr, minStr].filter(Boolean).join(' ');
                            })()}
                          </Typography>
                        )}
                        {index === 4 && values.date_end && values.time_end && (
                          <Typography variant='caption' sx={{ ml: 1, color: 'text.secondary' }}>
                            {new Date(values.date_end).toLocaleString([], {
                              dateStyle: 'long',
                            })}
                          </Typography>
                        )}
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      {/* Title and Slug */}
                      {activeStep === 0 && (
                        <>
                          <GenericFormikInput
                            autoFocus
                            label='Title'
                            name='title'
                            value={values.title || ''}
                            onChange={(e: any) => {
                              handleChange(e);
                              debouncedSetSlug(e.target.value, setFieldValue);
                            }}
                            fullWidth
                            sx={{ mb: 2, mt: 1 }}
                            size='small'
                          />
                          <GenericFormikInput
                            name='slug'
                            label='Slug'
                            value={values.slug || ''}
                            onChange={handleChange}
                            fullWidth
                            size='small'
                          />
                        </>
                      )}
                      {/* Recurrence */}
                      {activeStep === 1 && (
                        <Box mt={1} mb={2}>
                          <FormControl fullWidth>
                            <InputLabel id='recurrence-label'>Recurrence</InputLabel>
                            <Select
                              labelId='recurrence-label'
                              value={values.reoccurrence}
                              label='Recurrence'
                              onChange={(e) => handleChange(e)}
                              name='reoccurrence'
                            >
                              <StyledMenuItem value={ReoccurrenceEnum.once}>
                                Does not repeat
                              </StyledMenuItem>
                              <StyledMenuItem value={ReoccurrenceEnum.daily}>Daily</StyledMenuItem>
                              <StyledMenuItem value={ReoccurrenceEnum.weekly}>
                                Weekly
                              </StyledMenuItem>
                              <StyledMenuItem value={ReoccurrenceEnum.monthly}>
                                Monthly
                              </StyledMenuItem>
                              <StyledMenuItem value={ReoccurrenceEnum.yearly}>
                                Yearly
                              </StyledMenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                      )}
                      {/* Start Date */}
                      {activeStep === 2 && (
                        <Box mb={2}>
                          <Typography variant='caption'>{recurrenceStartSubtext}</Typography>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              label='Start Date'
                              minDate={new Date()}
                              value={values.date_start ? new Date(values.date_start) : null}
                              onAccept={(newValue) => {
                                handleStartDateChange(
                                  newValue,
                                  setFieldValue,
                                  values.date_end,
                                  duration,
                                );
                              }}
                              onChange={(newValue) => {
                                handleStartDateChange(
                                  newValue,
                                  setFieldValue,
                                  values.date_end,
                                  duration,
                                );
                              }}
                              renderInput={(params) => (
                                <TextField {...params} fullWidth sx={{ mt: 2 }} />
                              )}
                              componentsProps={{
                                actionBar: {
                                  actions: ['today'],
                                },
                              }}
                            />
                            {errors && errors.date_start && (
                              <Typography variant='caption' color='error'>
                                {errors.date_start}
                              </Typography>
                            )}
                          </LocalizationProvider>
                        </Box>
                      )}

                      {activeStep === 3 && (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <Box mt={1} mb={2}>
                            {/* Start Time */}
                            <StartTimePicker
                              value={values.date_start}
                              onChange={(value) => {
                                const newStart = parseTimeStringToDate(value);
                                const baseDate = new Date(values.date_start || new Date());

                                const newDate = new Date(
                                  baseDate.getFullYear(),
                                  baseDate.getMonth(),
                                  baseDate.getDate(),
                                  newStart.getHours(),
                                  newStart.getMinutes(),
                                  0,
                                  0,
                                );

                                setFieldValue('date_start', newDate.toISOString());
                                setFieldTouched('date_start', true);
                                calculateAndSetEndDate(
                                  newDate.toISOString(),
                                  duration,
                                  setFieldValue,
                                );
                              }}
                            />
                            {/* Duration */}
                            <DurationPicker
                              value={duration}
                              onChange={(value) => {
                                const [hours, minutes] = value.split(':').map(Number);
                                const totalMinutes = hours * 60 + minutes;
                                setDuration(totalMinutes);
                                values.date_start &&
                                  calculateAndSetEndDate(
                                    values.date_start,
                                    totalMinutes,
                                    setFieldValue,
                                  );
                              }}
                            />
                          </Box>
                        </LocalizationProvider>
                      )}
                      {activeStep === 4 && isRecurring && (
                        <Box mb={2}>
                          <Typography variant='caption'>{`You are scheduling an event that recurs ${values.reoccurrence}. The date selected here represents when the series ends.`}</Typography>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            {/* End Date */}
                            <DatePicker
                              minDate={new Date(values.date_start || '')}
                              label={'End Date'}
                              value={values.date_end ? new Date(values.date_end) : null}
                              onAccept={(newValue) => {
                                if (newValue && !isNaN(new Date(newValue).getTime())) {
                                  setFieldValue('date_end', newValue.toISOString());
                                }
                              }}
                              onChange={(newValue) => {
                                if (newValue && !isNaN(new Date(newValue).getTime())) {
                                  setFieldValue('date_end', newValue.toISOString());
                                }
                              }}
                              renderInput={(params) => (
                                <TextField {...params} fullWidth sx={{ mt: 2 }} />
                              )}
                              componentsProps={{
                                actionBar: {
                                  actions: ['today'],
                                },
                              }}
                            />
                            {errors && errors.date_end && (
                              <Typography variant='caption' color='error'>
                                {errors.date_end}
                              </Typography>
                            )}
                          </LocalizationProvider>
                        </Box>
                      )}
                      {/* Step Navigation Buttons */}
                      <Box>
                        {!isLastStep && (
                          <Box display={'flex'}>
                            <Button
                              variant='contained'
                              fullWidth
                              disabled={nextDisabled()}
                              onClick={() => {
                                normalizeDateStartAndEnd({
                                  values,
                                  setFieldValue,
                                  duration,
                                  timeOptions: generateTimeSlots(30),
                                });

                                setActiveStep((prev) => prev + 1);
                              }}
                            >
                              Next
                            </Button>
                            <Button
                              fullWidth
                              variant='outlined'
                              disabled={isSaving}
                              onClick={() => {
                                if (activeStep === 0) {
                                  handleOnClose();
                                } else {
                                  setActiveStep((prev) => prev - 1);
                                }
                              }}
                              sx={{ ml: 1 }}
                            >
                              {activeStep > 0 ? 'Back' : 'Cancel'}
                            </Button>
                          </Box>
                        )}
                      </Box>
                      {isLastStep && (
                        <Box display={'flex'} width={'100%'} justifyContent={'space-between'}>
                          <LoadingButton
                            disabled={!(dirty && isValid)}
                            loading={isSaving}
                            fullWidth
                            variant='contained'
                            color='primary'
                            onClick={() => {
                              // Only auto-calculate end date if it's a one-time session
                              if (
                                values.reoccurrence === ReoccurrenceEnum.once &&
                                values.date_start
                              ) {
                                const start = new Date(values.date_start);
                                const end = addDurationToDate(start, duration);

                                setFieldValue('date_end', end.toISOString(), false);
                                setFieldValue('time_end', end.toISOString(), false);
                              }
                              handleSubmit();
                            }}
                            sx={{ mr: 1 }}
                          >
                            Save
                          </LoadingButton>
                          <Button
                            fullWidth
                            variant='outlined'
                            color='primary'
                            disabled={isSaving}
                            onClick={() => setActiveStep((prev) => prev - 1)}
                            sx={{ ml: 1 }}
                          >
                            Back
                          </Button>
                          <Button
                            disabled={activeStep === 0}
                            onClick={() => {
                              setActiveStep(0);
                              resetForm();
                            }}
                          >
                            Reset
                          </Button>
                        </Box>
                      )}
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </RightExpandableDrawer>
          </Form>
        );
      }}
    </Formik>
  );
};

export default CreateNewSessionDrawer;
